import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const r = await login(email, password);
    setBusy(false);
    if (!r.ok) {
      toast.error(r.error || "Error de autenticación");
      return;
    }
    toast.success(`Bienvenido, ${r.user.name}`);
    const role = r.user.role;
    navigate(role === "superadmin" ? "/admin" : role === "consejo" ? "/consejo" : "/productor");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Hero */}
      <div className="relative hidden lg:block">
        <img
          src="https://images.unsplash.com/photo-1495539406979-bf61750d38ad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxmYXJtZXIlMjBmaWVsZHxlbnwwfHx8fDE3Nzg0NDAwNzl8MA&ixlib=rb-4.1.0&q=85"
          alt="Campo extremeño"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-900/85 via-stone-900/40 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-end p-12 text-white">
          <div className="flex items-center gap-3 mb-6">
            <img src="/logo-pimenton.png" alt="DOP Pimentón de la Vera" className="h-14 w-14 object-contain bg-white rounded-xl p-1 shadow-lg" />
            <div className="font-heading text-xl font-semibold">DOP Pimentón de la Vera</div>
          </div>
          <h1 className="text-4xl xl:text-5xl font-heading font-bold leading-tight max-w-md">
            Gestión documental oficial del Consejo Regulador.
          </h1>
          <p className="mt-4 text-stone-200 max-w-sm leading-relaxed">
            Trazabilidad, control y validación de toda la documentación de productores e industrias.
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 md:p-12 paprika-bg">
        <Card className="w-full max-w-md p-8 md:p-10 rounded-2xl shadow-xl border-stone-200">
          <div className="lg:hidden flex items-center gap-3 mb-6">
            <img src="/logo-pimenton.png" alt="DOP" className="h-12 w-12 object-contain" />
            <div className="font-heading font-semibold">DOP Pimentón de la Vera</div>
          </div>
          <h2 className="text-3xl font-heading font-bold text-stone-900">Iniciar sesión</h2>
          <p className="mt-2 text-stone-600">Accede a tu panel documental.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com" data-testid="input-login-email" className="mt-1.5" />
            </div>
            <div>
              <div className="flex justify-between items-center">
                <Label htmlFor="password">Contraseña</Label>
                <Link to="/forgot" className="text-xs text-primary hover:underline" data-testid="link-forgot">¿Olvidaste tu contraseña?</Link>
              </div>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" data-testid="input-login-password" className="mt-1.5" />
            </div>
            <Button type="submit" disabled={busy} className="w-full h-11 bg-primary hover:bg-primary-hover text-white"
              data-testid="btn-login-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Entrar"}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-stone-600">
            ¿Eres productor o industria? <Link to="/register" className="text-primary font-medium hover:underline" data-testid="link-register">Crea tu cuenta</Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
